import React, { useState, useMemo } from "react";
import { DeleteOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { ConfigTypeSingle, ICustomFilter } from "@shared/lib/types";
import { Typography, Slider, Space, Button } from "antd";
import { SelectButton } from "@shared/ui/Select";
import HelpPopOver from "../../../shared/ui/HelpPopover";
import { updateObjectAtIndex } from "../lib/utils/updateObjectAtIndex";
import { useGetInfluencerStore } from "../lib/store/useGetInfluencerStore";

interface Props extends ConfigTypeSingle {
  isOnSearch?: boolean;
}

const { Paragraph, Title } = Typography;

const CustomMultiSelect: React.FC<Props> = ({
  desc,
  facetName,
  isSearchable,
  title,
  placeHolder,
  isOnSearch = false,
}) => {
  const [visible, setVisible] = useState<boolean>(isOnSearch);
  const [searchQuery, setSearchQuery] = useState("");

  const { facets, selectedFilters, updateFilters } = useGetInfluencerStore();

  const getFacetName = (id: string) => {
    return facets[facetName]?.find((item) => item._id === id)?._id || id;
  };

  const options = useMemo(() => {
    const rawOptions = (facets[facetName] || []).map((d) => ({
      value: d._id,
      label: d._id,
    }));
    if (searchQuery) {
      return rawOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return rawOptions;
  }, [facets, facetName, searchQuery]);

  const currentValue = useMemo(() => {
    const selectedList = (selectedFilters[facetName] as ICustomFilter[]) || [];
    return selectedList.map((item) => item?.name);
  }, [selectedFilters, facetName]);

  const handleChange = (values: string[] | undefined) => {
    if (Array.isArray(values)) {
      const oldArrayKeys: string[] = (
        (selectedFilters[facetName] || []) as Array<{
          [key: string]: any;
        }>
      ).map((item) => item?.name);

      let updatedArrayVal: ICustomFilter[] = [];

      values.forEach((valItem) => {
        const oldIdx = oldArrayKeys.indexOf(valItem);

        if (oldIdx === -1) {
          updatedArrayVal = [
            ...updatedArrayVal,
            { name: valItem, percentage: 0 },
          ];
        } else {
          updatedArrayVal = [
            ...updatedArrayVal,
            (selectedFilters[facetName] as ICustomFilter[])[oldIdx],
          ];
        }
      });

      updateFilters(facetName, updatedArrayVal);
    }
  };

  return (
    <div style={{ display: "grid", width: "100%", gap: "5px" }}>
      {!isOnSearch && (
        <Space
          onClick={() => setVisible(!visible)}
          size={"small"}
          align="center"
          style={{
            marginBottom: "5px",
            color: visible ? "#1677ff" : undefined,
          }}
        >
          {visible ? <PlusOutlined size={19} /> : <MinusOutlined size={19} />}
          <Title
            level={5}
            style={{ marginBlock: 0, color: visible ? "#1677ff" : undefined }}
          >
            {title}
          </Title>
          {desc && <HelpPopOver customText={desc} color="black" />}
        </Space>
      )}
      {visible && (
        <>
          <SelectButton
            size="middle"
            mode="multiple"
            placeholder={placeHolder || "Select options"}
            options={options}
            value={currentValue}
            onChange={handleChange}
            searchable={isSearchable}
            searchValue={searchQuery || undefined}
            onSearchChange={setSearchQuery}
            onDropdownVisibleChange={(open) => {
              if (!open) setSearchQuery("");
            }}
            withFooterActions
            allowClear={false}
          />

          {selectedFilters[facetName] && (
            <div style={{ marginTop: "10px", display: "grid", gap: "10px" }}>
              {Array.isArray(selectedFilters[facetName]) &&
                (selectedFilters[facetName] as ICustomFilter[])?.map(
                  (arrItem: any, idx: number) => (
                    <div key={idx} style={{ padding: "8px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <Paragraph style={{ margin: 0, fontSize: "12px", color: "#475569" }}>
                          More than <b>{arrItem?.percentage}</b>% {title} is{" "}
                          <b>{getFacetName(arrItem?.name)}</b>
                        </Paragraph>
                        <Button
                          danger
                          type="link"
                          style={{ padding: 0, height: "auto" }}
                          onClick={() => {
                            let oldFacet = selectedFilters[facetName] || [];
                            oldFacet = (oldFacet as Array<any>).filter(
                              (_, dIdex) => dIdex !== idx
                            );
                            updateFilters(facetName, oldFacet);
                          }}
                          icon={<DeleteOutlined />}
                        />
                      </div>
                      <Slider
                        min={0}
                        max={100}
                        onChange={(val) => {
                          let updatedArray = updateObjectAtIndex(
                            selectedFilters[facetName] as ICustomFilter[],
                            "percentage",
                            val,
                            idx
                          );
                          updateFilters(
                            facetName,
                            updatedArray as ICustomFilter[]
                          );
                        }}
                        value={Number(arrItem?.percentage || 0)}
                      />
                    </div>
                  )
                )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomMultiSelect;
